import { useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'

import {
  PremiumFeatureSubscription,
  PremiumFeaturesDocument,
  SubscriptionStatus,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'

import { PremiumFeatureFromHook } from './components/FeatureCard'
import { createFakeSubscription } from './utils'

atlas`
query PremiumFeatures {
  premiumFeatures {
    id
    name
    description
    internalUrl
    marketingUrl
    image
    groupId
    groupLevel
    openTrialDaysRemaining
    prices {
      id
      amount
      frequency
      trialDays
    }
    subscriptions {
      id
      priceId
      accountAccessCount
      expiryDate
      startDate
      frequency
      status
      isOwner
      isTrial
    }
  }
}`

/**
 * This is a view model that will let us keep a "current" subscription
 * so we can work with a single one instead of adding search logic
 * in multiple places. This simplifies our UI logic immensely. In case we
 * need information about any other subscription we can just check the
 * buckets by status.
 */
export type SubscriptionsView = {
  count: number
  current: PremiumFeatureSubscription | null
  activeSubscriptions: PremiumFeatureSubscription[]
  scheduledSubscriptions: PremiumFeatureSubscription[]
  cancelledSubscriptions: PremiumFeatureSubscription[]
  errorSubscriptions: PremiumFeatureSubscription[]
  processingSubscriptions: PremiumFeatureSubscription[]
}

type arrayViewBuckets = Exclude<keyof SubscriptionsView, 'count' | 'current'>

const buckets: Partial<Record<SubscriptionStatus, arrayViewBuckets>> = {
  [SubscriptionStatus.ACTIVE]: 'activeSubscriptions',
  [SubscriptionStatus.SCHEDULED]: 'scheduledSubscriptions',
  [SubscriptionStatus.CANCELLED]: 'cancelledSubscriptions',
  [SubscriptionStatus.ERROR]: 'errorSubscriptions',
  [SubscriptionStatus.PROCESSING]: 'processingSubscriptions',
}
function subscriptionsReducer(
  view: SubscriptionsView,
  subscription: PremiumFeatureFromHook['subscriptions'][0],
): SubscriptionsView {
  const status = subscription.status

  // sort the subscriptions in buckets based on status
  const bucket = buckets[status]
  if (bucket) {
    view[bucket].push(subscription)
  }

  /**
   * the only scenario where we have 2 subscriptions in a feature (which means
   * view.current will be truthy) is when we have a price change scheduled.
   * In that case, the current subscription we want to show is the CANCELLED
   * one, the SCHEDULED one will just give us information about the start date
   * of the new price.
   * That's why, if there's a view.current, we skip the assignment when the
   * subscription is SCHEDULED. Is most likely the existing view.current
   * is status CANCELLED and we want that one to stay there.
   */
  if (!view?.current || subscription.status !== SubscriptionStatus.SCHEDULED) {
    view.current = subscription
  }

  return view
}

export function getSubscriptionsView(
  subscriptions: PremiumFeatureFromHook['subscriptions'],
): SubscriptionsView {
  return subscriptions.reduce<SubscriptionsView>(subscriptionsReducer, {
    current: null,
    count: subscriptions.length,
    activeSubscriptions: [],
    scheduledSubscriptions: [],
    cancelledSubscriptions: [],
    errorSubscriptions: [],
    processingSubscriptions: [],
  })
}

export const useGetFeatures = () => {
  const [params] = useSearchParams()
  // used to know what feature id was worked with in the stripe portal
  const urlFeatureId = params.get('featureId')
    ? decodeURIComponent(params.get('featureId')!)
    : undefined
  const afterSuccess = params.get('success') === 'true'
  const [pollInterval, setPollInterval] = useState<number | undefined>(
    undefined,
  )

  const { data, loading, error } = useQuery(PremiumFeaturesDocument, {
    client: atlasClient,
    pollInterval,
  })

  const processingFeature =
    urlFeatureId && afterSuccess
      ? data?.premiumFeatures?.find((ft) => ft.id === urlFeatureId)
      : undefined

  const fakeSubscriptionNeeded =
    processingFeature &&
    (processingFeature.subscriptions?.length === 0 ||
      processingFeature?.subscriptions?.some(
        (sub) =>
          // this can technically give in to a bug, but so far I've only seen this scenario
          // by recreating it manually. also, we might not actually need this last condition
          // at all
          sub.status === SubscriptionStatus.CANCELLED ||
          sub.status === SubscriptionStatus.EXPIRED,
      ))

  const patchedData = {
    premiumFeatures: data?.premiumFeatures
      ?.map((ft) => {
        if (fakeSubscriptionNeeded && ft.id === urlFeatureId) {
          return {
            ...ft,
            subscriptions: [
              createFakeSubscription(SubscriptionStatus.PROCESSING),
            ],
          }
        }

        /** when a feature comes without a subscription, we should ALWAYS assume
         * that the feature is AVAILABLE_FOR_PURCHASE. Maybe we should do this in
         * the back but for now we do in the front
         */
        if (ft.subscriptions.length === 0) {
          return {
            ...ft,
            subscriptions: [
              createFakeSubscription(SubscriptionStatus.AVAILABLE_FOR_PURCHASE),
            ],
          }
        }

        return ft
      })
      .map((ft) => ({
        ...ft,
        subscriptionsView: getSubscriptionsView(ft.subscriptions),
      })),
  }

  useEffect(() => {
    setPollInterval(fakeSubscriptionNeeded ? 10000 : undefined)
  }, [fakeSubscriptionNeeded])

  return { data: patchedData, loading, error }
}
